/**
 * Family Planner — the operating system for a family's education life.
 * Not a calendar grid. A living timeline that orchestrates the entire TriSphere ecosystem.
 *
 * Premium redesign with ambient hero, glassmorphic widgets, visual day timeline,
 * smart preparation cards, countdown tiles, term progress, and rich event modal.
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, X, ChevronLeft, ChevronRight, Calendar, Clock, MapPin,
  Users, Edit3, Trash2, AlertTriangle, GraduationCap, Bus, Heart,
  Trophy, Star, Zap, Bell, Copy, Share2, ExternalLink,
  CheckCircle, Phone, Mail, CreditCard, FileText, Send, Video,
  Repeat, Tag, Flag, Search, BookOpen, AlertCircle, ArrowRight,
  Download, Printer, Settings, User, Sun, Coffee, Moon,
  CloudRain, Map as MapIcon, ClipboardList, TrendingUp,
  Circle, CheckCircle2, Clock3, Sparkles, Droplets, Wind,
  Thermometer, ChevronDown, Filter, MoreHorizontal,
  Award, Activity, Zap as ZapIcon,
} from "lucide-react";

// ─── Apple design tokens ────────────────────────────────────────────────────
const C = {
  text: "#1C1917", textMid: "#3C3530", textMuted: "#8B6F47", textLight: "#A68A5A",
  bg: "#EDE4D4", card: "#FFFFFF", border: "#E2D6C0", cardBorder: "#E2D6C0",
  accent: "#7B1416", accentLight: "#F5EFE4",
  green: "#C4A882", greenLight: "#E3F9E5",
  red: "#B33A1A", redLight: "#FEE5E5",
  orange: "#C4A882", orangeLight: "#FFF4E5",
  teal: "#C4A882", tealLight: "#E6FFFE",
  purple: "#8B6F47", purpleLight: "#F5E6FD",
  pink: "#FF2D55", pinkLight: "#FFE8ED",
  blue: "#7B1416", blueLight: "#F5EFE4",
  gold: "#FFCC00", goldLight: "#FFFADC",
  amber: "#C4A882", amberLight: "#FFF4E5",
} as const;

const EASE = [0.4, 0, 0.2, 1] as const;
const PLANNER_GRADIENT = "linear-gradient(135deg, #FF2D55 0%, #7B1416 100%)";
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const DAYS_FULL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

// ─── Event types ────────────────────────────────────────────────────────────
const ETYPES = [
  { k:"general",  label:"General",   color:C.blue,    icon:Calendar,      parentActions:["Note in planner"] },
  { k:"class",    label:"Class",     color:C.accent,  icon:GraduationCap, parentActions:["Message teacher","View syllabus"] },
  { k:"meeting",  label:"Meeting",   color:C.purple,  icon:Users,         parentActions:["Confirm attendance","Reschedule","View agenda"] },
  { k:"deadline", label:"Deadline",  color:C.red,    icon:AlertTriangle, parentActions:["Mark complete","Request extension"] },
  { k:"trip",     label:"Trip",      color:C.amber,   icon:Bus,           parentActions:["Confirm consent","Pay trip fee","View itinerary"] },
  { k:"sports",   label:"Sports",    color:C.green,   icon:Trophy,        parentActions:["Confirm participation","Track results"] },
  { k:"exam",     label:"Exam",      color:C.pink,    icon:Star,          parentActions:["View syllabus","Message teacher","Note in planner"] },
  { k:"holiday",  label:"Holiday",   color:C.gold,    icon:Zap,           parentActions:["Plan family activity","Book transport"] },
  { k:"health",   label:"Health",    color:C.red,     icon:Heart,         parentActions:["Confirm appointment","Upload records"] },
  { k:"finance",  label:"Fee Due",   color:C.amber,   icon:CreditCard,    parentActions:["Pay now","Set reminder","Download invoice"] },
];

function getEType(k: string) { return ETYPES.find(e => e.k === k) ?? ETYPES[0]; }

// ─── Helpers ────────────────────────────────────────────────────────────────
function isSameDay(a: Date, b: Date) { return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }
function addDays(d: Date, n: number) { const r=new Date(d); r.setDate(r.getDate()+n); return r; }
function startOfWeek(d: Date) { const r=new Date(d); r.setDate(d.getDate()-d.getDay()); r.setHours(0,0,0,0); return r; }
function fmtDate(iso: string) { return new Date(iso).toLocaleDateString("en-UG",{weekday:"long",month:"long",day:"numeric",year:"numeric"}); }
function fmtShort(iso: string) { return new Date(iso).toLocaleDateString("en-UG",{month:"short",day:"numeric"}); }
function fmtTime(iso: string) { return new Date(iso).toLocaleTimeString("en-UG",{hour:"2-digit",minute:"2-digit"}); }

// ─── Types ──────────────────────────────────────────────────────────────────
interface CalEvent {
  id: string; title: string; description?: string; event_type: string;
  start_at: string; end_at?: string; location?: string; color: string;
  all_day?: boolean; status?: string; user_id?: string;
  child_name?: string;
}
type ViewMode = "agenda"|"week"|"month";

const BLANK = { title:"", description:"", event_type:"general", date:"", end_date:"", time:"09:00", end_time:"10:00", location:"", color:C.blue, all_day:false };

// ─── Demo events ─────────────────────────────────────────────────────────────
function getDemoEvents(): CalEvent[] {
  const today = new Date();
  const mkDate = (h: number, m: number = 0, offset = 0) => {
    const d = new Date(today); d.setDate(d.getDate() + offset); d.setHours(h, m, 0, 0); return d.toISOString();
  };
  return [
    { id:"de1", title:"Bus Arrives", event_type:"general", start_at: mkDate(6,30), color:C.orange, location:"Route B · Driver: Moses", child_name:"Emma", user_id:"school" },
    { id:"de2", title:"School Begins", event_type:"class", start_at: mkDate(8,0), color:C.green, location:"Attendance pending", child_name:"Emma", user_id:"school" },
    { id:"de3", title:"Science Practical", event_type:"class", start_at: mkDate(10,30), color:C.teal, location:"Laboratory 2", child_name:"Emma", user_id:"school" },
    { id:"de4", title:"Lunch", event_type:"general", start_at: mkDate(12,30), color:C.amber, location:"Meal Plan A", child_name:"Emma", user_id:"school" },
    { id:"de5", title:"Football Practice", event_type:"sports", start_at: mkDate(15,45), color:C.green, location:"Sports Field", child_name:"Emma", user_id:"school" },
    { id:"de6", title:"Bus Arriving Home", event_type:"general", start_at: mkDate(17,5), color:C.orange, location:"ETA 18 mins", child_name:"Emma", user_id:"school" },
    { id:"de7", title:"Science Trip", event_type:"trip", start_at: mkDate(6,45, 1), color:C.amber, location:"Uganda Museum · Bus departs 6:45 AM", child_name:"Emma", user_id:"school", description:"Bring packed lunch, water bottle, hat, and signed consent form." },
    { id:"de8", title:"Sports Day", event_type:"sports", start_at: mkDate(9,0, 2), color:C.green, location:"Sports Field · Bring PE Kit", child_name:"Emma", user_id:"school" },
    { id:"de9", title:"Parent Meeting", event_type:"meeting", start_at: mkDate(14,30, 3), color:C.purple, location:"Principal Hall · 2:30 PM", user_id:"school" },
    { id:"de10", title:"Fees Due", event_type:"finance", start_at: mkDate(0,0, 5), color:C.red, location:"Term 3 · UGX 1,850,000", all_day:true, user_id:"school" },
    { id:"de11", title:"Midterm Exams Begin", event_type:"exam", start_at: mkDate(8,0, 12), color:C.pink, location:"All subjects", child_name:"Emma", user_id:"school" },
    { id:"de12", title:"End of Term", event_type:"holiday", start_at: mkDate(0,0, 42), color:C.gold, location:"Term 2 concludes", all_day:true, user_id:"school" },
  ];
}

// ─── Daily Timeline (Apple Fitness style) ───────────────────────────────────
function DailyTimeline({ events, day }: { events: CalEvent[]; day: Date }) {
  const dayEvents = events.filter(e => isSameDay(new Date(e.start_at), day)).sort((a,b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
  const now = new Date();
  const isToday = isSameDay(day, now);

  if (dayEvents.length === 0) return (
    <div className="text-center py-12">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="inline-flex">
        <Calendar className="h-12 w-12 mx-auto mb-3" style={{ color: C.textLight }} />
      </motion.div>
      <p className="text-sm font-medium" style={{ color: C.textMuted }}>Nothing planned today</p>
      <p className="text-xs mt-1" style={{ color: C.textLight }}>Enjoy the free time!</p>
    </div>
  );

  return (
    <div className="relative pl-8">
      <div className="absolute left-3 top-1 bottom-1 w-px" style={{ background: C.border }} />
      {dayEvents.map((ev, i) => {
        const et = getEType(ev.event_type);
        const Icon = et.icon;
        const evTime = new Date(ev.start_at);
        const isPast = isToday && evTime < now;
        const isActive = isToday && Math.abs(evTime.getTime() - now.getTime()) < 3600000;

        return (
          <motion.div
            key={ev.id}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.06, duration: 0.3 }}
            className="relative flex items-start gap-4 pb-5 last:pb-0"
          >
            <motion.div
              whileHover={{ scale: 1.15 }}
              className="absolute -left-[20px] w-5 h-5 rounded-full flex items-center justify-center shrink-0"
              style={{ background: isPast ? C.textLight : ev.color, boxShadow: "0 0 0 3px white" }}
            >
              {isPast ? <CheckCircle2 className="w-3 h-3 text-white" /> : <Icon className="w-2.5 h-2.5 text-white" />}
            </motion.div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-mono font-semibold" style={{ color: isPast ? C.textMuted : C.text }}>
                  {ev.all_day ? "All day" : fmtTime(ev.start_at)}
                </span>
                {isActive && (
                  <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ repeat: Infinity, duration: 1.5 }} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: C.accent, color: "white" }}>
                    Now
                  </motion.span>
                )}
                {ev.child_name && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${ev.color}15`, color: ev.color }}>{ev.child_name}</span>}
              </div>
              <p className="text-sm font-semibold tracking-tight" style={{ color: isPast ? C.textMuted : C.text }}>{ev.title}</p>
              {ev.location && <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{ev.location}</p>}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

// ─── Smart Preparation Card ─────────────────────────────────────────────────
function SmartPreparation({ event }: { event: CalEvent }) {
  const et = getEType(event.event_type);
  const Icon = et.icon;
  const isTrip = event.event_type === "trip";

  const checklist = isTrip
    ? [
        { label: "Packed Lunch", done: false },
        { label: "Water Bottle", done: false },
        { label: "Hat", done: false },
        { label: "Signed Consent Form", done: false },
      ]
    : event.event_type === "sports"
      ? [{ label: "PE Kit", done: false }, { label: "Water Bottle", done: false }, { label: "Sunscreen", done: false }]
      : [{ label: "Confirm attendance", done: false }];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      className="relative overflow-hidden rounded-[24px] border bg-white shadow-premium"
      style={{ borderColor: C.cardBorder }}
    >
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${et.color}, ${et.color}88)` }} />
      <div className="px-5 py-4 border-b border-[#E2D6C0]/60 flex items-center gap-3">
        <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: `${et.color}15` }}>
          <Icon className="h-5 w-5" style={{ color: et.color }} />
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>Tomorrow's Preparation</p>
          <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>{event.title}</h3>
        </div>
        <div className="ml-auto">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: C.amberLight }}>
            <Clock className="h-3.5 w-3.5" style={{ color: C.amber }} />
            <span className="text-[10px] font-semibold" style={{ color: C.amber }}>Tomorrow</span>
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="space-y-2.5 mb-4">
          {checklist.map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }} className="flex items-center gap-3 group cursor-pointer">
              <div className="transition-transform group-hover:scale-110">
                {item.done ? <CheckCircle2 className="h-5 w-5" style={{ color: C.green }} /> : <Circle className="h-5 w-5" style={{ color: C.textLight }} />}
              </div>
              <span className="text-sm" style={{ color: C.textMid }}>{item.label}</span>
            </motion.div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-[#E2D6C0]/60">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>Departure</p>
            <p className="text-sm font-semibold mt-0.5" style={{ color: C.text }}>{fmtTime(event.start_at)}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>Weather</p>
            <p className="text-sm font-semibold mt-0.5" style={{ color: C.text }}>27°C</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>Rain</p>
            <p className="text-sm font-semibold mt-0.5" style={{ color: C.amber }}>15%</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Coming Up section ──────────────────────────────────────────────────────
function ComingUp({ events, onSelect }: { events: CalEvent[]; onSelect: (e: CalEvent) => void }) {
  const now = new Date();
  const upcoming = events.filter(e => new Date(e.start_at) >= now).sort((a,b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()).slice(0, 6);

  return (
    <div className="space-y-3">
      {upcoming.map((ev, i) => {
        const et = getEType(ev.event_type);
        const Icon = et.icon;
        const diff = Math.ceil((new Date(ev.start_at).getTime() - now.getTime()) / 3600000);
        const label = diff <= 1 ? "In 1 hour" : diff < 24 ? `In ${diff} hours` : diff < 48 ? "Tomorrow" : `In ${Math.floor(diff/24)} days`;

        return (
          <motion.button
            key={ev.id}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.06 }}
            whileHover={{ x: 4, y: -2 }}
            onClick={() => onSelect(ev)}
            className="w-full text-left flex items-center gap-3 p-4 rounded-[16px] border bg-white transition-all shadow-premium"
            style={{ borderColor: C.cardBorder }}
          >
            <div className="h-11 w-11 rounded-[12px] flex items-center justify-center shrink-0 transition-transform hover:scale-110" style={{ background: `${et.color}15` }}>
              <Icon className="h-5 w-5" style={{ color: et.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold tracking-tight truncate" style={{ color: C.text }}>{ev.title}</p>
              <p className="text-xs truncate" style={{ color: C.textMuted }}>{ev.location ?? ""}</p>
            </div>
            <span className="text-xs font-semibold shrink-0 px-2.5 py-1 rounded-full" style={{ background: `${et.color}15`, color: et.color }}>{label}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

// ─── Countdown section ┐
function CountdownSection({ events, onSelect }: { events: CalEvent[]; onSelect: (e: CalEvent) => void }) {
  const now = new Date();
  const countdowns = events
    .filter(e => new Date(e.start_at) >= now)
    .sort((a,b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
    .slice(0, 4);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {countdowns.map((ev, i) => {
        const et = getEType(ev.event_type);
        const days = Math.floor((new Date(ev.start_at).getTime() - now.getTime()) / 86400000);
        return (
          <motion.button
            key={ev.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.08 }}
            whileHover={{ y: -4, scale: 1.03 }}
            onClick={() => onSelect(ev)}
            className="relative flex flex-col items-center gap-1 p-5 rounded-[18px] border bg-white transition-all shadow-premium overflow-hidden"
            style={{ borderColor: C.cardBorder }}
          >
            <div className="absolute top-0 left-0 right-0 h-1" style={{ background: et.color }} />
            <p className="text-3xl font-bold tracking-tight" style={{ color: et.color }}>{days}</p>
            <p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>{days === 1 ? "Day" : "Days"}</p>
            <p className="text-xs font-medium text-center mt-1" style={{ color: C.text }}>{ev.title}</p>
          </motion.button>
        );
      })}
    </div>
  );
}

// ─── Term Progress ──────────────────────────────────────────────────────────
function TermProgress() {
  const week = 6;
  const totalWeeks = 10;
  const pct = (week / totalWeeks) * 100;

  return (
    <div className="relative overflow-hidden rounded-[24px] border bg-white shadow-premium" style={{ borderColor: C.cardBorder }}>
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${C.purple}, ${C.pink})` }} />
      <div className="px-5 py-4 border-b border-[#E2D6C0]/60 flex items-center gap-3">
        <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: C.purpleLight }}>
          <TrendingUp className="h-5 w-5" style={{ color: C.purple }} />
        </div>
        <div>
          <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>School Journey</h3>
          <p className="text-xs" style={{ color: C.textMuted }}>Term 2 · Week {week} of {totalWeeks}</p>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: C.bg }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: EASE }}
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${C.purple}, ${C.pink})` }}
            />
          </div>
          <span className="text-sm font-semibold" style={{ color: C.text }}>{Math.round(pct)}%</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {["Midterms", "Holiday", "PTA", "Prize Giving"].map((item, i) => (
            <motion.span key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.06 }} className="text-xs px-3 py-1.5 rounded-full" style={{ background: C.bg, color: C.textMid }}>{item}</motion.span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Event Modal ────────────────────────────────────────────────────────────
function EventModal({ event, onClose, onDelete, onEdit }: { event: CalEvent; onClose(): void; onDelete(id:string): void; onEdit(ev:CalEvent): void }) {
  const et = getEType(event.event_type);
  const Icon = et.icon;
  const isSchool = !event.user_id || event.user_id === "school";
  const isPast = new Date(event.start_at) < new Date();
  const daysAway = Math.ceil((new Date(event.start_at).getTime()-Date.now())/86400000);

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ background:"rgba(0,0,0,0.4)", backdropFilter:"blur(16px)" }} onClick={onClose}>
        <motion.div initial={{ y:40, scale:0.95, opacity:0 }} animate={{ y:0, scale:1, opacity:1 }} exit={{ y:40, scale:0.95, opacity:0 }}
          transition={{ type:"spring", damping:30, stiffness:320 }}
          className="relative w-full max-w-lg rounded-[24px] overflow-hidden shadow-2xl bg-white" onClick={e=>e.stopPropagation()}>
          <div className="relative p-6 pb-5 overflow-hidden" style={{ background:`linear-gradient(135deg, ${event.color} 0%, ${event.color}bb 100%)` }}>
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-20" style={{ background: "white" }} />
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background:"rgba(255,255,255,0.25)", backdropFilter:"blur(8px)" }}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-white/70 text-[10px] font-semibold uppercase tracking-tight">{et.label}</span>
                  {isSchool && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ background:"rgba(255,255,255,0.25)" }}>SCHOOL</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {!isSchool && <button onClick={()=>onEdit(event)} className="p-2 rounded-xl text-white/80 hover:bg-white/20 transition"><Edit3 className="w-4 h-4" /></button>}
                <button onClick={onClose} className="p-2 rounded-xl text-white/80 hover:bg-white/20 transition"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <h2 className="relative text-white text-2xl font-semibold mt-4 leading-tight tracking-tight">{event.title}</h2>
            {!isPast && daysAway >= 0 && (
              <div className="relative mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background:"rgba(255,255,255,0.2)" }}>
                <Clock className="w-3.5 h-3.5 text-white" />
                <span className="text-white text-xs font-semibold">{daysAway === 0 ? "Today" : daysAway === 1 ? "Tomorrow" : `In ${daysAway} days`}</span>
              </div>
            )}
          </div>
          <div className="p-6 space-y-3">
            <div className="flex items-start gap-3"><div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background:C.bg }}><Calendar className="w-4 h-4" style={{ color:C.textMuted }} /></div><div><p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textLight }}>Date</p><p className="text-sm font-medium mt-0.5" style={{ color:C.text }}>{event.all_day ? `${fmtShort(event.start_at)} · All day` : fmtDate(event.start_at)}</p></div></div>
            {!event.all_day && <div className="flex items-start gap-3"><div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background:C.bg }}><Clock className="w-4 h-4" style={{ color:C.textMuted }} /></div><div><p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textLight }}>Time</p><p className="text-sm font-medium mt-0.5" style={{ color:C.text }}>{fmtTime(event.start_at)}{event.end_at ? ` – ${fmtTime(event.end_at)}` : ""}</p></div></div>}
            {event.location && <div className="flex items-start gap-3"><div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background:C.bg }}><MapPin className="w-4 h-4" style={{ color:C.textMuted }} /></div><div><p className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textLight }}>Location</p><p className="text-sm font-medium mt-0.5" style={{ color:C.text }}>{event.location}</p></div></div>}
            {event.description && <div className="p-4 rounded-[16px] text-sm leading-relaxed" style={{ background:C.bg, color:C.text }}>{event.description}</div>}
          </div>
          {et.parentActions && et.parentActions.length > 0 && (
            <div className="px-6 pb-2"><p className="text-[10px] font-semibold uppercase tracking-tight mb-3" style={{ color:C.textMuted }}>Your Actions</p>
              <div className="flex flex-wrap gap-2">{et.parentActions.map(action => {
                const iconMap: Record<string, React.ElementType> = { "Pay now":CreditCard, "Confirm attendance":CheckCircle, "Message teacher":Mail, "View syllabus":BookOpen, "Note in planner":BookOpen, "Set reminder":Bell, "Download invoice":Download, "Confirm consent":CheckCircle, "Pay trip fee":CreditCard, "View itinerary":ExternalLink, "Confirm participation":CheckCircle, "Track results":Trophy, "Request extension":Send, "Mark complete":CheckCircle, "Upload records":FileText, "Confirm appointment":Calendar, "Reschedule":Repeat, "View agenda":FileText, "Plan family activity":Users, "Book transport":Bus, "Add reminder":Bell, "Share event":Share2 };
                const A = iconMap[action] ?? ArrowRight;
                return <motion.button key={action} whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }} onClick={() => toast.success(`${action} — action registered`)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition" style={{ background:`${event.color}10`, color:event.color, border:`1px solid ${event.color}30` }}><A className="w-3.5 h-3.5" />{action}</motion.button>;
              })}</div>
            </div>
          )}
          <div className="px-6 py-4 flex items-center justify-between border-t mt-4" style={{ borderColor:C.cardBorder }}>
            <button onClick={()=>{ navigator.clipboard.writeText(event.title+" — "+fmtDate(event.start_at)); toast.success("Copied!"); }} className="flex items-center gap-1.5 text-xs font-medium transition hover:opacity-70" style={{ color:C.textMuted }}><Copy className="w-3.5 h-3.5" />Copy</button>
            <div className="flex items-center gap-2">
              <button onClick={()=>toast.success("Reminder set")} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition hover:opacity-80" style={{ background:`${event.color}15`, color:event.color }}><Bell className="w-3.5 h-3.5" />Remind me</button>
              {!isSchool && <button onClick={()=>{ onDelete(event.id); onClose(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition hover:opacity-80" style={{ background:`${C.red}15`, color:C.red }}><Trash2 className="w-3.5 h-3.5" />Delete</button>}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Create/Edit Drawer ─────────────────────────────────────────────────────
function CreateDrawer({ open, onClose, onSave, prefill }: { open:boolean; onClose():void; onSave(data:any):void; prefill?:Partial<typeof BLANK> }) {
  const [form, setForm] = useState({ ...BLANK });
  useEffect(() => { if (open) setForm({ ...BLANK, ...prefill }); }, [open, JSON.stringify(prefill)]);
  const set = (k:string, v:any) => setForm(p => ({...p,[k]:v}));

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background:"rgba(0,0,0,0.4)", backdropFilter:"blur(8px)" }} onClick={onClose}>
          <motion.div initial={{ y:60, opacity:0 }} animate={{ y:0, opacity:1 }} exit={{ y:60, opacity:0 }}
            transition={{ type:"spring", damping:28, stiffness:300 }}
            className="w-full sm:max-w-lg rounded-t-[24px] sm:rounded-[24px] overflow-y-auto bg-white"
            style={{ maxHeight:"92vh" }} onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 z-10 bg-white" style={{ borderColor:C.cardBorder }}>
              <h3 className="font-semibold text-base tracking-tight" style={{ color:C.text }}>New Event</h3>
              <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#EDE4D4]"><X className="w-5 h-5" style={{ color:C.textMuted }} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textMuted }}>Event type</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ETYPES.map(et => (
                    <button key={et.k} onClick={() => set("event_type", et.k)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition"
                      style={form.event_type === et.k ? { background:et.color, color:"white" } : { background:C.bg, color:C.textMuted, border:`1px solid ${C.border}` }}>
                      <et.icon className="w-3.5 h-3.5" />{et.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textMuted }}>Title *</label>
                <input value={form.title} onChange={e=>set("title",e.target.value)} placeholder="Event title" className="w-full mt-1 px-4 py-3 rounded-xl text-sm focus:outline-none" style={{ border:`1px solid ${C.border}`, background:C.bg, color:C.text }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textMuted }}>Start date *</label><input type="date" value={form.date} onChange={e=>set("date",e.target.value)} className="w-full mt-1 px-4 py-3 rounded-xl text-sm focus:outline-none" style={{ border:`1px solid ${C.border}`, background:C.bg, color:C.text }} /></div>
                <div><label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textMuted }}>End date</label><input type="date" value={form.end_date} onChange={e=>set("end_date",e.target.value)} className="w-full mt-1 px-4 py-3 rounded-xl text-sm focus:outline-none" style={{ border:`1px solid ${C.border}`, background:C.bg, color:C.text }} /></div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <div onClick={() => set("all_day", !form.all_day)} className="relative w-10 h-6 rounded-full transition-colors" style={{ background: form.all_day ? C.accent : C.border }}>
                  <div className="absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform" style={{ left: form.all_day ? "22px" : "4px" }} />
                </div>
                <span className="text-sm font-medium" style={{ color:C.text }}>All day event</span>
              </label>
              {!form.all_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textMuted }}>Start time</label><input type="time" value={form.time} onChange={e=>set("time",e.target.value)} className="w-full mt-1 px-4 py-3 rounded-xl text-sm focus:outline-none" style={{ border:`1px solid ${C.border}`, background:C.bg, color:C.text }} /></div>
                  <div><label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textMuted }}>End time</label><input type="time" value={form.end_time} onChange={e=>set("end_time",e.target.value)} className="w-full mt-1 px-4 py-3 rounded-xl text-sm focus:outline-none" style={{ border:`1px solid ${C.border}`, background:C.bg, color:C.text }} /></div>
                </div>
              )}
              <div><label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textMuted }}>Location</label><input value={form.location} onChange={e=>set("location",e.target.value)} placeholder="e.g. School Hall" className="w-full mt-1 px-4 py-3 rounded-xl text-sm focus:outline-none" style={{ border:`1px solid ${C.border}`, background:C.bg, color:C.text }} /></div>
              <div><label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color:C.textMuted }}>Description</label><textarea rows={3} value={(form as any).description ?? ""} onChange={e=>set("description",e.target.value)} placeholder="Additional details..." className="w-full mt-1 px-4 py-3 rounded-xl text-sm focus:outline-none resize-none" style={{ border:`1px solid ${C.border}`, background:C.bg, color:C.text }} /></div>
            </div>
            <div className="sticky bottom-0 px-6 py-4 border-t flex gap-3 bg-white" style={{ borderColor:C.cardBorder }}>
              <button onClick={onClose} className="flex-1 py-3 rounded-xl text-sm font-semibold" style={{ background:C.bg, color:C.textMuted }}>Cancel</button>
              <button onClick={() => { if(!form.title.trim()||!form.date) { toast.error("Title and date required"); return; } onSave(form); }} className="flex-1 py-3 rounded-xl text-sm font-semibold text-white" style={{ background:C.accent }}>Save Event</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export function ParentCalendar({ students }: { students?: { id:string; full_name:string }[] }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const now = new Date();

  const [view, setView] = useState<ViewMode>("agenda");
  const [currentMonth, setCurrentMonth] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(now));
  const [selectedEvent, setSelectedEvent] = useState<CalEvent|null>(null);
  const [creating, setCreating] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<Partial<typeof BLANK>>({});
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch events
  const { data: myEvents = [] } = useQuery<CalEvent[]>({
    queryKey: ["cal_mine", user?.id], enabled: !!user, refetchInterval: 30000,
    queryFn: async () => { const { data } = await supabase.from("calendar_events").select("*").eq("user_id",user!.id).order("start_at"); return (data??[]) as CalEvent[]; },
  });
  const { data: schoolEvents = [] } = useQuery<CalEvent[]>({
    queryKey: ["cal_school"], refetchInterval: 60000,
    queryFn: async () => { const { data } = await supabase.from("school_calendar_events").select("*").order("start_at"); return (data??[]).map((e:any) => ({ ...e, user_id:"school" })) as CalEvent[]; },
  });

  const demoEvents = useMemo(() => getDemoEvents(), []);
  const allEvents = useMemo(() => {
    let evs = [...demoEvents, ...myEvents, ...schoolEvents];
    if (searchQuery) evs = evs.filter(e => e.title.toLowerCase().includes(searchQuery.toLowerCase()));
    return evs;
  }, [demoEvents, myEvents, schoolEvents, searchQuery]);

  const createMut = useMutation({
    mutationFn: async (form: typeof BLANK & { description?: string }) => {
      if (!user) throw new Error("Not signed in");
      const et = getEType(form.event_type);
      const start = form.all_day ? new Date(form.date).toISOString() : new Date(`${form.date}T${form.time}`).toISOString();
      const end = form.end_date ? (form.all_day ? new Date(form.end_date).toISOString() : new Date(`${form.end_date}T${form.end_time}`).toISOString()) : undefined;
      const { error } = await supabase.from("calendar_events").insert({ user_id:user.id, title:form.title.trim(), description:form.description?.trim()||null, event_type:form.event_type, start_at:start, end_at:end||null, location:form.location||null, color:et.color, all_day:form.all_day, status:"confirmed" });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Event created"); setCreating(false); qc.invalidateQueries({queryKey:["cal_mine"]}); },
    onError: (e:Error) => toast.error(e.message),
  });
  const deleteMut = useMutation({
    mutationFn: async (id:string) => { await supabase.from("calendar_events").delete().eq("id",id); },
    onSuccess: () => { qc.invalidateQueries({queryKey:["cal_mine"]}); toast.success("Event deleted"); },
  });

  // Calendar math
  const firstDow = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth()+1, 0).getDate();
  const weekDays = useMemo(() => Array.from({length:7},(_,i)=>addDays(currentWeek,i)), [currentWeek]);
  const eventsForDay = useCallback((d:Date) => allEvents.filter(e=>isSameDay(new Date(e.start_at),d)), [allEvents]);
  const monthName = currentMonth.toLocaleDateString("en-UG",{month:"long",year:"numeric"});

  // Tomorrow's events
  const tomorrowEvents = allEvents.filter(e => isSameDay(new Date(e.start_at), addDays(now, 1)));
  const tomorrowPrep = tomorrowEvents[0];

  // Greeting
  const greeting = useMemo(() => {
    const h = now.getHours();
    if (h < 12) return { text: "Good morning", icon: Sun, phase: "Morning", gradient: "linear-gradient(135deg, #FFF4E5 0%, #FFF8F0 60%, #FFFDF5 100%)", glow: C.orange };
    if (h < 17) return { text: "Good afternoon", icon: Coffee, phase: "Afternoon", gradient: "linear-gradient(135deg, #FFE8ED 0%, #FFF0F3 60%, #FFF8FA 100%)", glow: C.pink };
    return { text: "Good evening", icon: Moon, phase: "Evening", gradient: "linear-gradient(135deg, #F5E6FD 0%, #FAF0FF 60%, #FDFAFF 100%)", glow: C.purple };
  }, []);
  const GreetingIcon = greeting.icon;

  // Stats
  const todayEvents = allEvents.filter(e => isSameDay(new Date(e.start_at), now));
  const upcomingCount = allEvents.filter(e => new Date(e.start_at) >= now).length;
  const weather = { temp: 24, condition: "Partly Cloudy", icon: Sun, humidity: 62, wind: 12 };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-5xl mx-auto">
      {/* ═════════ AMBIENT HERO ═════════ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="relative overflow-hidden rounded-[24px] border shadow-float"
        style={{ background: PLANNER_GRADIENT, borderColor: "rgba(255,255,255,0.15)" }}
      >
        <motion.div className="absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-20" style={{ background: "radial-gradient(circle, white, transparent 70%)" }} animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.25, 0.15] }} transition={{ duration: 6, repeat: Infinity }} />
        <motion.div className="absolute -left-16 -bottom-16 h-52 w-52 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #D4B89A, transparent 70%)" }} animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 8, repeat: Infinity, delay: 2 }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

        <div className="relative px-6 sm:px-10 py-8 sm:py-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-white/15 backdrop-blur-md">
              <GreetingIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50">Family Planner · {greeting.phase}</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{now.toLocaleDateString("en-UG", { weekday: "long" })}</h2>
            </div>
          </div>

          <p className="text-lg text-white/70 max-w-lg">
            {now.toLocaleDateString("en-UG", { month: "long", day: "numeric", year: "numeric" })} · {todayEvents.length} {todayEvents.length === 1 ? "event" : "events"} today.
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { label: "Today", value: todayEvents.length, color: "text-white", icon: Calendar },
              { label: "Upcoming", value: upcomingCount, color: "text-white", icon: Clock3 },
              { label: "This Week", value: weekDays.reduce((s, d) => s + eventsForDay(d).length, 0), color: "text-white", icon: Activity },
            ].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08 }} className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10">
                <s.icon className="h-4 w-4 text-white/60" />
                <span className="text-lg font-bold text-white">{s.value}</span>
                <span className="text-xs text-white/50">{s.label}</span>
              </motion.div>
            ))}
            {/* Weather */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10">
              <weather.icon className="h-5 w-5 text-white/80" />
              <span className="text-lg font-bold text-white">{weather.temp}°</span>
              <div className="flex flex-col">
                <span className="text-[9px] font-semibold uppercase tracking-wider text-white/50">{weather.condition}</span>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-0.5 text-[9px] text-white/40"><Droplets className="h-2.5 w-2.5" />{weather.humidity}%</span>
                  <span className="flex items-center gap-0.5 text-[9px] text-white/40"><Wind className="h-2.5 w-2.5" />{weather.wind}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* ═════════ SEARCH + VIEW SWITCHER + NEW EVENT ═════════ */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: C.textMuted }} />
          <input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search events…" className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{ border:`1px solid ${C.border}`, background:C.card, color:C.text }} />
        </div>
        <div className="flex rounded-xl overflow-hidden" style={{ border:`1px solid ${C.border}` }}>
          {(["agenda","week","month"] as const).map(v => (
            <button key={v} onClick={()=>setView(v)} className="px-3 py-2 text-xs font-semibold capitalize transition" style={view===v ? { background:C.accent, color:"white" } : { background:C.card, color:C.textMuted }}>{v}</button>
          ))}
        </div>
        <button onClick={() => { setCreatePrefill({ date:new Date().toISOString().split("T")[0] }); setCreating(true); }} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white transition hover:opacity-90" style={{ background:C.accent }}>
          <Plus className="w-4 h-4" />New Event
        </button>
      </div>

      {/* ═════════ AGENDA VIEW ═════════ */}
      {view === "agenda" && (
        <div className="space-y-6">
          {/* Daily Timeline */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }} className="relative overflow-hidden rounded-[24px] border bg-white shadow-premium" style={{ borderColor: C.cardBorder }}>
            <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: C.accent }} />
            <div className="px-5 py-4 border-b border-[#E2D6C0]/60 flex items-center gap-3">
              <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: C.accentLight }}>
                <Calendar className="h-5 w-5" style={{ color: C.accent }} />
              </div>
              <div>
                <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>Today's Timeline</h3>
                <p className="text-xs" style={{ color: C.textMuted }}>{todayEvents.length} {todayEvents.length === 1 ? "event" : "events"} scheduled</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: C.greenLight }}>
                <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: C.green }} />
                <span className="text-[10px] font-semibold" style={{ color: C.green }}>Live</span>
              </div>
            </div>
            <div className="p-5 sm:p-6">
              <DailyTimeline events={allEvents} day={now} />
            </div>
          </motion.div>

          {/* Smart Preparation */}
          {tomorrowPrep && <SmartPreparation event={tomorrowPrep} />}

          {/* Coming Up */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ background: C.accentLight }}>
                <Clock3 className="h-4 w-4" style={{ color: C.accent }} />
              </div>
              <h3 className="text-lg font-semibold tracking-tight" style={{ color: C.text }}>Coming Up</h3>
            </div>
            <ComingUp events={allEvents} onSelect={setSelectedEvent} />
          </div>

          {/* Countdowns */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ background: C.amberLight }}>
                <Zap className="h-4 w-4" style={{ color: C.amber }} />
              </div>
              <h3 className="text-lg font-semibold tracking-tight" style={{ color: C.text }}>Countdown</h3>
            </div>
            <CountdownSection events={allEvents} onSelect={setSelectedEvent} />
          </div>

          {/* Term Progress */}
          <TermProgress />
        </div>
      )}

      {/* ═════════ WEEK VIEW ═════════ */}
      {view === "week" && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-[24px] border bg-white shadow-premium" style={{ borderColor: C.cardBorder }}>
          <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: C.cardBorder }}>
            <button onClick={() => setCurrentWeek(addDays(currentWeek,-7))} className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[#EDE4D4]"><ChevronLeft className="w-4 h-4" style={{ color: C.textMuted }} /></button>
            <p className="font-semibold text-base tracking-tight" style={{ color: C.text }}>{weekDays[0].toLocaleDateString("en-UG",{month:"short",day:"numeric"})} – {weekDays[6].toLocaleDateString("en-UG",{month:"short",day:"numeric",year:"numeric"})}</p>
            <button onClick={() => setCurrentWeek(addDays(currentWeek,7))} className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[#EDE4D4]"><ChevronRight className="w-4 h-4" style={{ color: C.textMuted }} /></button>
          </div>
          <div className="p-5 space-y-6">
            {weekDays.map((day, i) => {
              const dayEvs = eventsForDay(day).sort((a,b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
              const isToday = isSameDay(day, now);
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-[14px] flex flex-col items-center justify-center ${isToday ? "text-white" : ""}`} style={isToday ? { background: C.accent } : { background: C.bg }}>
                      <span className="text-[10px] font-semibold uppercase">{day.toLocaleDateString("en-UG",{weekday:"short"})}</span>
                      <span className="text-lg font-semibold">{day.getDate()}</span>
                    </div>
                    <div className="flex-1 h-px" style={{ background: C.border }} />
                    <span className="text-xs" style={{ color: C.textMuted }}>{dayEvs.length} event{dayEvs.length!==1?"s":""}</span>
                  </div>
                  {dayEvs.length > 0 ? (
                    <div className="pl-2 space-y-2">
                      {dayEvs.map(ev => {
                        const et = getEType(ev.event_type);
                        return (
                          <motion.button key={ev.id} whileHover={{ x: 4 }} onClick={() => setSelectedEvent(ev)} className="w-full text-left flex items-center gap-3 p-3 rounded-[14px] transition" style={{ background:`${ev.color}08`, border:`1px solid ${ev.color}25` }}>
                            <div className="w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0" style={{ background:`${ev.color}20` }}><et.icon className="w-4 h-4" style={{ color:ev.color }} /></div>
                            <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate" style={{ color:C.text }}>{ev.title}</p>{!ev.all_day && <p className="text-xs mt-0.5" style={{ color:C.textMuted }}>{fmtTime(ev.start_at)}</p>}</div>
                            {ev.child_name && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background:`${ev.color}15`, color:ev.color }}>{ev.child_name}</span>}
                          </motion.button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="pl-2 text-xs" style={{ color: C.textLight }}>Nothing planned</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* ═════════ MONTH VIEW ═════════ */}
      {view === "month" && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-[24px] border bg-white shadow-premium" style={{ borderColor: C.cardBorder }}>
          <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: C.cardBorder }}>
            <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(),currentMonth.getMonth()-1,1))} className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[#EDE4D4]"><ChevronLeft className="w-4 h-4" style={{ color: C.textMuted }} /></button>
            <p className="font-semibold text-base tracking-tight" style={{ color: C.text }}>{monthName}</p>
            <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(),currentMonth.getMonth()+1,1))} className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[#EDE4D4]"><ChevronRight className="w-4 h-4" style={{ color: C.textMuted }} /></button>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-7 mb-2">
              {DAYS_SHORT.map((d,i) => <div key={d} className="text-center py-2 text-[10px] font-semibold uppercase tracking-tight" style={{ color: i===0||i===6 ? C.textLight : C.textMuted }}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({length:firstDow}).map((_,i) => <div key={`e${i}`} className="min-h-[80px]" />)}
              {Array.from({length:daysInMonth}).map((_,i) => {
                const day = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i+1);
                const dayEvs = eventsForDay(day);
                const isToday = isSameDay(day, now);
                return (
                  <motion.div key={i} whileHover={{ scale:1.02 }} onClick={() => { if (dayEvs[0]) setSelectedEvent(dayEvs[0]); }} className="min-h-[80px] rounded-[16px] p-2 cursor-pointer transition-all relative overflow-hidden" style={{ background: isToday ? `${C.accent}12` : "transparent", border: `1.5px solid ${isToday ? `${C.accent}60` : "transparent"}` }}>
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold" style={isToday ? { background:C.accent, color:"white" } : { color:C.text }}>{i+1}</span>
                    <div className="mt-1 space-y-0.5">
                      {dayEvs.slice(0,2).map(ev => <div key={ev.id} className="text-[10px] font-semibold rounded-lg px-1.5 py-0.5 truncate" style={{ background:`${ev.color}22`, color:ev.color }}>{ev.title}</div>)}
                      {dayEvs.length > 2 && <span className="text-[9px] font-semibold" style={{ color:C.textMuted }}>+{dayEvs.length-2} more</span>}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* Overlays */}
      <AnimatePresence>
        {selectedEvent && <EventModal event={selectedEvent} onClose={()=>setSelectedEvent(null)} onDelete={id=>deleteMut.mutate(id)} onEdit={ev=>{ setSelectedEvent(null); setCreatePrefill({ title:ev.title, date:ev.start_at.split("T")[0], event_type:ev.event_type, location:ev.location||"" }); setCreating(true); }} />}
      </AnimatePresence>
      <CreateDrawer open={creating} onClose={()=>setCreating(false)} onSave={form => createMut.mutate(form as any)} prefill={createPrefill} />
    </div>
  );
}

export default ParentCalendar;
